/**
 * Bepaalt de kleurstaal voor een optiewaarde. Alleen weergave: de waarde zelf,
 * de prijs en wat naar de leverancier gaat blijven ongemoeid.
 *
 * Volgorde:
 *  1. een expliciete hex op de waarde (value.hex, metadata.hex, ...), tenzij
 *     die automatisch is gegenereerd (generatedSwatch / colorSwatchAutoAssigned);
 *  2. een leverancierscode in tekst of metadata: Pantone/PMS, RAL of een hex;
 *  3. "geen", transparant, matglas of meerkleurig;
 *  4. het kleurnamenwoordenboek (nl/en/de/fr, textielkleurnamen), inclusief
 *     samenstellingen ("Dunkelblau", "Bordeauxrot") en tweekleurig
 *     ("Zwart met wit");
 *  5. de automatisch gegenereerde hex als die geen neutraal grijs is;
 *  6. anders: niet opgelost (neutrale staal).
 *
 * De Nederlandse leveranciersnaam (metadata.printcomName) gaat voor het
 * vertaalde label, zodat elke taal dezelfde staal krijgt.
 */
import { normalizeColorText } from './color-option';
import {
  COLOR_NAMES,
  CONNECTORS,
  FROSTED_WORDS,
  METALLIC_NAMES,
  MODIFIERS,
  MULTI_WORDS,
  NONE_PREFIXES,
  PMS_COATED,
  PMS_METALLIC_GOLD,
  PMS_METALLIC_SILVER,
  PMS_NAMED,
  RAL_CLASSIC,
  RAL_METALLIC,
  TRANSPARENT_WORDS,
} from './color-swatch-data';

export type ColorSwatchKind =
  | 'solid'
  | 'metallic'
  | 'two-tone'
  | 'multi'
  | 'transparent'
  | 'frosted'
  | 'none'
  | 'unresolved';

export type ColorSwatchSource = 'explicit' | 'pms' | 'ral' | 'hex' | 'name' | 'pattern' | 'weak' | 'none';

export interface ColorSwatch {
  kind: ColorSwatchKind;
  /** CSS-waarde voor `background` (kan een verloop zijn). */
  background: string;
  /** Opgeloste kleuren (hex), leeg voor patronen zonder kleur. */
  colors: string[];
  source: ColorSwatchSource;
}

/** Neutrale staal voor een waarde waarvan we de kleur niet kennen. */
export const UNRESOLVED_SWATCH_BACKGROUND = '#e5e7eb';
/** Het grijs dat de oude generator aan elke onbekende textielkleur gaf. */
const GENERATED_PLACEHOLDER_GREY = '#9CA3AF';

const CHECKER = 'repeating-conic-gradient(#d1d5db 0 25%, #ffffff 0 50%) 50% / 8px 8px';
const NONE_BACKGROUND =
  'linear-gradient(to top right, #ffffff calc(50% - 1px), #9ca3af calc(50% - 1px), #9ca3af calc(50% + 1px), #ffffff calc(50% + 1px))';
const MULTI_BACKGROUND = 'conic-gradient(#ef4444, #f59e0b, #facc15, #22c55e, #3b82f6, #8b5cf6, #ec4899, #ef4444)';
const FROSTED_BACKGROUND = 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 45%, #f9fafb 55%, #e5e7eb 100%)';

const UNRESOLVED: ColorSwatch = { kind: 'unresolved', background: UNRESOLVED_SWATCH_BACKGROUND, colors: [], source: 'none' };

// ---------------------------------------------------------------- hex helpers

export function normalizeHex(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const match = input.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (!match) return null;
  let hex = match[1];
  if (hex.length === 3) hex = hex.split('').map((char) => char + char).join('');
  return `#${hex.slice(0, 6).toUpperCase()}`;
}

function toRgb(hex: string): [number, number, number] {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((channel) => Math.round(Math.max(0, Math.min(255, channel))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

export function mixHex(a: string, b: string, weightOfB = 0.5): string {
  const [ar, ag, ab] = toRgb(a);
  const [br, bg, bb] = toRgb(b);
  return toHex([ar + (br - ar) * weightOfB, ag + (bg - ag) * weightOfB, ab + (bb - ab) * weightOfB]);
}

const lighten = (hex: string, amount: number) => mixHex(hex, '#FFFFFF', amount);
const darken = (hex: string, amount: number) => mixHex(hex, '#000000', amount);

function rgba(hex: string, alpha: number) {
  const [r, g, b] = toRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function metallicBackground(hex: string) {
  return `linear-gradient(135deg, ${darken(hex, 0.35)} 0%, ${lighten(hex, 0.55)} 42%, ${hex} 58%, ${darken(hex, 0.25)} 100%)`;
}

function swatchFromColors(colors: string[], options: { metallic?: boolean; transparent?: boolean; source: ColorSwatchSource }): ColorSwatch {
  const [first, second] = colors;
  if (options.transparent) {
    const tint = first ? `linear-gradient(${rgba(first, 0.55)}, ${rgba(first, 0.55)}), ` : '';
    return { kind: 'transparent', background: `${tint}${CHECKER}`, colors, source: options.source };
  }
  if (second && second !== first) {
    return {
      kind: 'two-tone',
      background: `linear-gradient(135deg, ${first} 0%, ${first} 50%, ${second} 50%, ${second} 100%)`,
      colors: [first, second],
      source: options.source,
    };
  }
  if (options.metallic) {
    return { kind: 'metallic', background: metallicBackground(first), colors: [first], source: options.source };
  }
  return { kind: 'solid', background: first, colors: [first], source: options.source };
}

// ------------------------------------------------------------ value fields

type AnyValue = Record<string, any> | null | undefined;

function metadataOf(value: AnyValue): Record<string, any> {
  const metadata = value?.metadata;
  return metadata && typeof metadata === 'object' ? metadata : {};
}

function explicitHexes(value: AnyValue): { primary: string | null; secondary: string | null; weak: boolean } {
  const metadata = metadataOf(value);
  const primary = [
    value?.hex,
    metadata.hex,
    value?.color,
    metadata.color,
    metadata.colorCode,
    metadata.colourCode,
    metadata.colorHex,
    value?.swatch,
    metadata.swatch,
    value?.previewColor,
    metadata.previewColor,
  ]
    .map(normalizeHex)
    .find(Boolean) || null;
  const secondary = [metadata.secondaryHex, value?.secondaryHex, metadata.secondaryColor].map(normalizeHex).find(Boolean) || null;
  const weak = Boolean(
    metadata.generatedSwatch || value?.generatedSwatch || metadata.colorSwatchAutoAssigned || value?.colorSwatchAutoAssigned,
  );
  return { primary, secondary, weak };
}

/** Teksten om op te zoeken, in volgorde van betrouwbaarheid. */
function candidateTexts(value: AnyValue): string[] {
  const metadata = metadataOf(value);
  const raw = [
    metadata.printcomName,
    value?.label,
    value?.name,
    value?.value,
    value?.id,
    metadata.colorName,
    metadata.colourName,
  ];
  const seen = new Set<string>();
  const texts: string[] = [];
  for (const entry of raw) {
    if (entry === null || entry === undefined) continue;
    const text = String(entry).trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    texts.push(text);
  }
  return texts;
}

/** Codevelden in metadata die een kale PMS- of RAL-code kunnen bevatten. */
function codeFieldTexts(value: AnyValue): string[] {
  const metadata = metadataOf(value);
  const texts: string[] = [];
  for (const key of ['pms', 'pantone', 'pantoneCode', 'pmsCode']) {
    if (metadata[key] !== undefined && metadata[key] !== null) texts.push(`pms ${metadata[key]}`);
  }
  for (const key of ['ral', 'ralCode']) {
    if (metadata[key] !== undefined && metadata[key] !== null) texts.push(`ral ${metadata[key]}`);
  }
  for (const key of ['colorCode', 'colourCode', 'supplierColorCode']) {
    if (typeof metadata[key] === 'string') texts.push(metadata[key]);
  }
  return texts;
}

// ------------------------------------------------------------------ codes

export function pmsToHex(code: string): { hex: string; metallic: boolean } | null {
  const cleaned = normalizeColorText(code).replace(/^(pms|pantone)\s?/, '').replace(/\s?(c|u|cp|up|tcx|tpx|m)$/, '').trim();
  if (PMS_COATED[cleaned]) {
    return { hex: PMS_COATED[cleaned], metallic: PMS_METALLIC_GOLD.has(cleaned) || PMS_METALLIC_SILVER.has(cleaned) };
  }
  if (PMS_METALLIC_GOLD.has(cleaned)) return { hex: '#85714D', metallic: true };
  if (PMS_METALLIC_SILVER.has(cleaned)) return { hex: '#8A8D8F', metallic: true };
  if (PMS_NAMED[cleaned]) return { hex: PMS_NAMED[cleaned], metallic: false };
  return null;
}

function codeSwatch(text: string): ColorSwatch | null {
  const hexMatch = text.match(/#([0-9a-f]{6}|[0-9a-f]{3})(?![0-9a-z])/i);
  if (hexMatch) {
    const hex = normalizeHex(hexMatch[0]);
    if (hex) return swatchFromColors([hex], { source: 'hex' });
  }

  const normalized = normalizeColorText(text);
  const pms = normalized.match(/(?:^|\s)(?:pms|pantone)\s?([0-9]{3,5})\s?(?:c|u|cp|up|tcx|tpx)?(?=\s|$)/);
  if (pms) {
    const resolved = pmsToHex(pms[1]);
    if (resolved) return swatchFromColors([resolved.hex], { metallic: resolved.metallic, source: 'pms' });
  }
  const pmsNamed = normalized.match(/(?:^|\s)(?:pms|pantone)\s([a-z][a-z0-9 ]*)$/);
  if (pmsNamed) {
    const words = pmsNamed[1].replace(/\s(c|u|cp)$/, '').split(' ');
    for (let length = words.length; length > 0; length -= 1) {
      const name = words.slice(0, length).join(' ');
      if (PMS_NAMED[name]) return swatchFromColors([PMS_NAMED[name]], { source: 'pms' });
    }
  }

  const ral = normalized.match(/(?:^|\s)ral\s?([0-9]{4})(?=\s|$)/);
  if (ral && RAL_CLASSIC[ral[1]]) {
    return swatchFromColors([RAL_CLASSIC[ral[1]]], { metallic: RAL_METALLIC.has(ral[1]), source: 'ral' });
  }
  return null;
}

// ------------------------------------------------------------------ names

type NameHit = { start: number; end: number; hex: string; key: string; metallic: boolean };

const COMPOUND_SUFFIXES = /(kleurig|kleurige|farben|farbig|farbe|coloured|colored|kleur)$/;

/** Basiskleuren die als staart van een onbekende samenstelling mogen gelden. */
const BASIC_COLOR_WORDS = new Set([
  'wit', 'weiss', 'white', 'blanc', 'zwart', 'schwarz', 'black', 'noir', 'grijs', 'grau', 'grey', 'gray', 'gris',
  'rood', 'rot', 'rouge', 'blauw', 'blau', 'blue', 'bleu', 'groen', 'grun', 'green', 'vert', 'geel', 'gelb', 'yellow', 'jaune',
  'oranje', 'orange', 'roze', 'rosa', 'pink', 'paars', 'lila', 'violett', 'purple', 'violet', 'bruin', 'braun', 'brown',
  'beige', 'goud', 'gold', 'zilver', 'silber', 'silver', 'turkis', 'turquoise', 'marine', 'navy', 'kaki', 'khaki', 'oliv', 'olive',
]);

/** Materiaalwoorden: wijken voor een echte kleur in dezelfde waarde ("aluminium voetplaten zwart"). */
const MATERIAL_WORDS = new Set([
  'aluminium', 'aluminum', 'alu', 'alimunium', 'inox', 'rvs', 'stainless steel', 'edelstahl', 'edelstahl rostfrei', 'metal', 'metaal',
  'metall', 'metale', 'chrome', 'chroom', 'chrom', 'wood', 'hout', 'holz', 'bois', 'houten',
]);

function compoundHit(token: string): { hex: string; key: string; metallic: boolean } | null {
  const stripped = token.replace(COMPOUND_SUFFIXES, '');
  if (stripped !== token && COLOR_NAMES[stripped]) {
    return { hex: COLOR_NAMES[stripped], key: stripped, metallic: METALLIC_NAMES.has(stripped) };
  }
  // Verbogen vorm: "rode", "blaue", "grüne" -> zonder -e/-en/-er/-es.
  for (const ending of ['en', 'er', 'es', 'e']) {
    if (stripped.endsWith(ending)) {
      const stem = stripped.slice(0, -ending.length);
      if (stem.length >= 3 && COLOR_NAMES[stem]) return { hex: COLOR_NAMES[stem], key: stem, metallic: METALLIC_NAMES.has(stem) };
    }
  }
  // Samenstelling: voorvoegsel + kleur ("dunkelblau", "bordeauxrot", "amalfigelb").
  for (let split = 2; split <= stripped.length - 3; split += 1) {
    const prefix = stripped.slice(0, split);
    const suffix = stripped.slice(split);
    const suffixHex = COLOR_NAMES[suffix];
    // "grijsmelange", "blaumeliert": kleur + modifier.
    if (!suffixHex && MODIFIERS[suffix] && COLOR_NAMES[prefix]) {
      return { hex: applyModifier(COLOR_NAMES[prefix], MODIFIERS[suffix]), key: prefix, metallic: MODIFIERS[suffix] === 'metallic' || METALLIC_NAMES.has(prefix) };
    }
    if (!suffixHex) continue;
    const modifier = MODIFIERS[prefix];
    if (modifier) {
      return { hex: applyModifier(suffixHex, modifier), key: suffix, metallic: modifier === 'metallic' || METALLIC_NAMES.has(suffix) };
    }
    const prefixHex = COLOR_NAMES[prefix];
    if (prefixHex) return { hex: mixHex(prefixHex, suffixHex), key: stripped, metallic: false };
    // Onbekend voorvoegsel ("amalfigelb", "teamrot"): alleen met een
    // basiskleur als staart, anders wordt "kunststof" stof en "carbone" bone.
    if (prefix.length >= 3 && BASIC_COLOR_WORDS.has(suffix)) {
      return { hex: suffixHex, key: suffix, metallic: METALLIC_NAMES.has(suffix) };
    }
  }
  return null;
}

function applyModifier(hex: string, modifier: string): string {
  switch (modifier) {
    case 'dark':
      return darken(hex, 0.35);
    case 'light':
      return lighten(hex, 0.45);
    case 'soft':
      return lighten(hex, 0.55);
    case 'heather':
      return mixHex(hex, '#B0B0B0', 0.3);
    default:
      return hex;
  }
}

function tokensOf(text: string): string[] {
  // "/", "&" en "+" zijn verbindingen ("Zwart / wit"); daarna normaliseren.
  const prepared = String(text).replace(/\s*[/&+]\s*/g, ' and ');
  return normalizeColorText(prepared)
    .split(' ')
    // Getallen en maten ("30", "240g", "04") zijn nooit een kleur.
    .filter((token) => token && !/^[0-9]+[a-z]{0,3}$/.test(token));
}

function containsPhrase(normalized: string, phrases: string[]) {
  return phrases.some((phrase) => new RegExp(`(^|\\s)${phrase}(\\s|$)`).test(normalized));
}

function nameSwatch(text: string): ColorSwatch | null {
  const normalized = normalizeColorText(text);
  if (!normalized) return null;

  if (NONE_PREFIXES.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix} `))) {
    return { kind: 'none', background: NONE_BACKGROUND, colors: [], source: 'pattern' };
  }
  if (containsPhrase(normalized, MULTI_WORDS)) {
    return { kind: 'multi', background: MULTI_BACKGROUND, colors: [], source: 'pattern' };
  }

  const tokens = tokensOf(text);
  const hits: NameHit[] = [];
  const consumed = new Set<number>();
  for (let index = 0; index < tokens.length; ) {
    let matched = false;
    for (let length = Math.min(4, tokens.length - index); length >= 1; length -= 1) {
      const phrase = tokens.slice(index, index + length).join(' ');
      const hex = COLOR_NAMES[phrase];
      if (hex) {
        hits.push({ start: index, end: index + length, hex, key: phrase, metallic: METALLIC_NAMES.has(phrase) });
        for (let position = index; position < index + length; position += 1) consumed.add(position);
        index += length;
        matched = true;
        break;
      }
    }
    if (matched) continue;
    const compound = MODIFIERS[tokens[index]] ? null : compoundHit(tokens[index]);
    if (compound) {
      hits.push({ start: index, end: index + 1, ...compound });
      consumed.add(index);
    }
    index += 1;
  }

  const modifiers = tokens
    .map((token, position) => (consumed.has(position) ? null : MODIFIERS[token]))
    .filter((modifier): modifier is NonNullable<typeof modifier> => Boolean(modifier));
  const metallic = modifiers.includes('metallic');
  const transparent = containsPhrase(normalized, TRANSPARENT_WORDS);

  if (hits.length === 0) {
    if (transparent) return swatchFromColors([], { transparent: true, source: 'pattern' });
    if (containsPhrase(normalized, FROSTED_WORDS)) {
      return { kind: 'frosted', background: FROSTED_BACKGROUND, colors: [], source: 'pattern' };
    }
    // Alleen "heather"/"melange": een gemêleerd grijs.
    if (modifiers.includes('heather')) return swatchFromColors(['#B0B0B0'], { source: 'name' });
    return null;
  }

  const colourHits = hits.length > 1 && hits.some((hit) => !MATERIAL_WORDS.has(hit.key))
    ? hits.filter((hit) => !MATERIAL_WORDS.has(hit.key))
    : hits;
  const [first, second] = colourHits;
  if (second && !transparent) {
    const between = tokens.slice(first.end, second.start);
    const joined = between.some((token) => CONNECTORS.has(token))
      && between.every((token) => CONNECTORS.has(token) || Boolean(MODIFIERS[token]));
    if (joined && first.hex !== second.hex) {
      return swatchFromColors([first.hex, second.hex], { source: 'name' });
    }
  }

  let hex = first.hex;
  if (second && second.start === first.end && second.hex !== first.hex) {
    // Twee kleurwoorden naast elkaar ("grijs blauw", "Heather Kelly" als losse
    // woorden): de mengkleur ligt het dichtst bij wat de klant ziet.
    hex = mixHex(first.hex, second.hex);
  }
  for (const modifier of modifiers) hex = applyModifier(hex, modifier);
  return swatchFromColors([hex], { metallic: metallic || first.metallic, transparent, source: 'name' });
}

// ----------------------------------------------------------------- public

const cache = new Map<string, ColorSwatch>();

function cacheKey(value: AnyValue) {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return `s:${String(value)}`;
  const metadata = metadataOf(value);
  return JSON.stringify([
    value.hex, value.color, value.swatch, value.previewColor, value.secondaryHex, value.generatedSwatch, value.colorSwatchAutoAssigned,
    metadata.hex, metadata.color, metadata.colorCode, metadata.colourCode, metadata.colorHex, metadata.swatch, metadata.previewColor,
    metadata.secondaryHex, metadata.secondaryColor, metadata.generatedSwatch, metadata.colorSwatchAutoAssigned, metadata.hexGeschat,
    metadata.pms, metadata.pantone, metadata.pantoneCode, metadata.pmsCode, metadata.ral, metadata.ralCode, metadata.supplierColorCode,
    metadata.printcomName, metadata.colorName, metadata.colourName, value.label, value.name, value.value, value.id,
  ]);
}

function resolveUncached(input: AnyValue | string): ColorSwatch {
  const value: AnyValue = typeof input === 'object' ? input : { label: input };
  const texts = candidateTexts(value);
  const explicit = explicitHexes(value);

  // Naaminformatie is ook nodig om een expliciete goudkleur als metallic te tonen.
  let named: ColorSwatch | null = null;
  for (const text of texts) {
    named = nameSwatch(text);
    if (named) break;
  }

  // 1. Expliciete, niet-gegenereerde hex.
  if (explicit.primary && !explicit.weak) {
    if (explicit.secondary && explicit.secondary !== explicit.primary) {
      return swatchFromColors([explicit.primary, explicit.secondary], { source: 'explicit' });
    }
    return swatchFromColors([explicit.primary], { metallic: named?.kind === 'metallic', source: 'explicit' });
  }

  // 2. Leverancierscodes (PMS, RAL, hex in de tekst).
  for (const text of [...codeFieldTexts(value), ...texts]) {
    const coded = codeSwatch(text);
    if (coded) return coded;
  }

  // 3 + 4. Patronen en kleurnamen.
  if (named) return named;

  // 5. Automatisch toegekende hex, behalve het neutrale plaatsvervangend grijs.
  if (explicit.primary && explicit.primary !== GENERATED_PLACEHOLDER_GREY) {
    if (explicit.secondary && explicit.secondary !== explicit.primary) {
      return swatchFromColors([explicit.primary, explicit.secondary], { source: 'weak' });
    }
    return swatchFromColors([explicit.primary], { source: 'weak' });
  }

  return UNRESOLVED;
}

export function resolveColorSwatch(value: AnyValue | string): ColorSwatch {
  const key = cacheKey(typeof value === 'object' ? value : { label: value });
  const cached = cache.get(key);
  if (cached) return cached;
  const resolved = resolveUncached(value);
  if (cache.size > 5000) cache.clear();
  cache.set(key, resolved);
  return resolved;
}

export function isResolvedColorSwatch(swatch: ColorSwatch): boolean {
  return swatch.kind !== 'unresolved';
}

export function hasResolvedColorSwatch(value: unknown): boolean {
  return isResolvedColorSwatch(resolveColorSwatch(value as AnyValue));
}

/** Zoektekst voor de kleurkiezer: label, leveranciersnaam en waarde, zonder accenten. */
export function colorSearchText(value: AnyValue, displayLabel?: string): string {
  const metadata = metadataOf(value);
  return normalizeColorText([displayLabel, value?.label, metadata.printcomName, value?.value].filter(Boolean).join(' '));
}
